import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { pdf } from '@react-pdf/renderer';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import TooltipTextPopupCustom from './child/TooltipTextPopupCustom';
import ReopenComplaintsReport from '../reports/ReopenComplaintsReport';


const TableDataLayerReopen = () => {
    const [complaints, setComplaints] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [qrCodes, setQrCodes] = useState({});
    const [qrDataUrl, setQrDataUrl] = useState({});
    const [downloading, setDownloading] = useState(false);
    const [fromDate, setFromDate] = useState(''); // datetime-local string
    const [toDate, setToDate] = useState('');     // datetime-local string
    const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
    const location = useLocation();
      const APP_BASE_URL = process.env.REACT_APP_URL;

    const fetchComplaints = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/backend/complaints/get-reopen-complaints`, {
            headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
            }
            });

            if (response.data.success) {
                setComplaints(response.data.data);
                generateQrCodes(response.data.data,APP_BASE_URL);
            } else {
                toast.error("Failed to fetch complaints.");
            }
        } catch (error) {
            console.error("Error fetching complaints:", error);
            toast.error("Error fetching complaints.");
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL,APP_BASE_URL]);

    const generateQrCodes = async (complaintsData,APP_BASE_URL) => {
        const newQrCodes = {};
        for (const complaint of complaintsData) {
            try {

                const qrCodeData = `${APP_BASE_URL}/complains/view/${complaint._id}`;
                const qrCodeUrl = await QRCode.toDataURL(JSON.stringify(qrCodeData));
                setQrDataUrl(qrCodeData);
                newQrCodes[complaint._id] = qrCodeUrl;
            } catch (error) {
                console.error("Error generating QR code:", error);
                newQrCodes[complaint._id] = null;
            }
        }
        setQrCodes(newQrCodes);
    };

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    // The date a complaint was re-opened (statusHistory "Reopened" entry; the
    // backend trims statusHistory to just that entry, but we still search it).
    const getReopenDate = (complaint) => {
        const hist = complaint.statusHistory || [];
        const reopened = [...hist].reverse().find((s) => s.status === 'Reopened') || hist[hist.length - 1];
        const d = reopened?.changedAt ? new Date(reopened.changedAt) : null;
        return d && !Number.isNaN(d.getTime()) ? d : null;
    };

    // The list AND the PDF both use this filtered set (search + re-open-date range).
    const normalizedSearchTerm = searchTerm.trim().replace(/^#/, '').toLowerCase();
    const fromTime = fromDate ? new Date(fromDate).getTime() : null;
    const toTime = toDate ? new Date(toDate).getTime() : null;
    const filteredComplaints = complaints.filter((complaint) => {
        const matchesSearch = String(complaint.complaint_number || '')
            .toLowerCase()
            .includes(normalizedSearchTerm);
        if (!matchesSearch) return false;
        if (fromTime == null && toTime == null) return true;
        const reopened = getReopenDate(complaint);
        if (!reopened) return false; // no re-open date -> excluded when a range is set
        const t = reopened.getTime();
        if (fromTime != null && t < fromTime) return false;
        if (toTime != null && t > toTime) return false;
        return true;
    });

    const clearDateRange = () => { setFromDate(''); setToDate(''); };

    // Download a PDF of the re-opened complaints currently shown — i.e. respecting
    // the search box AND the re-open-date range, so the PDF always matches the list.
    const handleDownloadReport = async () => {
        if (filteredComplaints.length === 0) {
            toast.info('No re-opened complaints match the current filter.');
            return;
        }
        setDownloading(true);
        try {
            const data = filteredComplaints.map((c) => ({
                complaint_number: c.complaint_number,
                complainant_name: c.complainant_name,
                complaint_reffered_by: c.complaint_reffered_by,
                complaint_detail: c.complaint_detail,
                dfo_remarks: c.dfo_remarks,
                reopen_remarks: getReopenEntry(c)?.userRemarks || '',
                reopen_date: getReopenDate(c),
            }));
            const generatedAt = new Date();
            let qrCodeUrl = '';
            try {
                qrCodeUrl = await QRCode.toDataURL(`${APP_BASE_URL}/reopen/list`, { width: 120 });
            } catch (qrErr) { /* QR is optional */ }

            const fmt = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);
            const rangeLabel = (fromDate || toDate)
                ? `Re-opened between ${fmt(fromDate) || 'the beginning'} and ${fmt(toDate) || 'now'}`
                : '';

            const blob = await pdf(
                <ReopenComplaintsReport reportData={data} generatedAt={generatedAt} qrCodeUrl={qrCodeUrl} rangeLabel={rangeLabel} />
            ).toBlob();

            const pad = (n) => String(n).padStart(2, '0');
            const stamp = `${pad(generatedAt.getDate())}-${pad(generatedAt.getMonth() + 1)}-${generatedAt.getFullYear()}`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Re-Open-Complaints-List-${stamp}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Report downloaded.');
        } catch (error) {
            console.error('Error building re-open report:', error);
            toast.error('Error building the report.');
        } finally {
            setDownloading(false);
        }
    };


    useEffect(() => {
        if (location.state?.showToast) {
            toast.success("Complaint Dispatched Successfully!");
        }
    }, [location.state]);

    const getPriorityClasses = (priority) => {
        switch (priority) {
            case "Low":
                return {
                    bg: 'bg-gradient-start-3',
                    border: 'border-info-300',
                    text: 'text-info-600',
                    glow: 'info-glow',
                    pulse: 'info-pulse',
                    wave: 'from-info-400 to-info-100',
                    ripple: 'info-100',
                    badge: 'bg-info-focus text-info-600',
                    gradient: 'bg-dark-info-gradient',
                    tooltip: 'tooltip-info'
                };
            case "Medium":
                return {
                    bg: 'bg-gradient-start-4',
                    border: 'border-success-300',
                    text: 'text-success-600',
                    glow: 'success-glow',
                    pulse: 'success-pulse',
                    wave: 'from-success-400/20 to-success-100/20',
                    ripple: 'success-100',
                    badge: 'bg-success-focus text-success-600',
                    gradient: 'bg-dark-success-gradient',
                    tooltip: 'tooltip-success'
                };
            case "High":
                return {
                    bg: 'bg-gradient-start-9',
                    border: 'border-warning-300/30',
                    text: 'text-warning-600',
                    glow: 'warning-glow',
                    pulse: 'warning-pulse',
                    wave: 'from-warning-400/20 to-warning-100/20',
                    ripple: 'warning-100',
                    badge: 'bg-warning-focus text-warning-600',
                    gradient: 'bg-dark-warning-gradient',
                    tooltip: 'tooltip-warning'
                };
            case "Critical":
                return {
                    bg: 'bg-gradient-start-5',
                    border: 'border-danger-300',
                    text: 'text-danger-600',
                    glow: 'danger-glow',
                    pulse: 'danger-pulse',
                    wave: 'from-danger-400 to-danger-100',
                    ripple: 'danger-100',
                    badge: 'bg-danger-focus text-danger-600',
                    gradient: 'bg-dark-danger-gradient',
                    tooltip: 'tooltip-danger'
                };
            default:
                return {
                    bg: 'bg-gradient-start-1',
                    border: 'border-secondary-300/30',
                    text: 'text-secondary-600',
                    glow: 'secondary-glow',
                    pulse: 'secondary-pulse',
                    wave: 'from-secondary-400/20 to-secondary-100/20',
                    ripple: 'secondary-100',
                    badge: 'bg-secondary text-secondary-600',
                    gradient: 'bg-dark-secondary-gradient'
                };
        }
    };

    const getTimeElapsed = (dateString) => {
        if (!dateString) return 'N/A';
        const now = new Date();
        const created = new Date(dateString);
        const diff = now - created;

        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h ago`;
        if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    // The most recent "Reopened" status-history entry (holds the re-open remark
    // and any attachment uploaded at re-open time).
    const getReopenEntry = (complaint) => {
        const hist = complaint.statusHistory || [];
        return [...hist].reverse().find((s) => s.status === 'Reopened') || hist[hist.length - 1] || null;
    };

    return (
        <div className="basic-data-table ">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="card-header bg-transparent border-bottom-0 pb-0 ">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                            Re-Open Complaints
                        </h2>
                        <p className="text-sm text-gray-500">
                            Complaints that were re-opened after being closed
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-success btn-sm d-flex align-items-center gap-2"
                            onClick={handleDownloadReport}
                            disabled={downloading || loading || filteredComplaints.length === 0}
                            title="Download a PDF of the re-opened complaints shown (respects the date range)"
                        >
                            {downloading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Preparing…
                                </>
                            ) : (
                                <>
                                    <Icon icon="mdi:file-pdf-box" style={{ fontSize: 18 }} /> Download PDF Report
                                </>
                            )}
                        </button>
                        <span className={`px-4 py-2 rounded-full text-xs font-semibold bg-warning-100/20 text-warning-800 border border-warning-300/30 warning-glow`}>
                            {complaints.length} Re-Open
                        </span>
                    </div>
                </div>
            </div>
            <div className="card-body pt-0">
                {/* Filter toolbar — search + re-open date-time range (also drives the PDF). */}
                <div className="mb-4 p-16 radius-12 bg-neutral-50 border border-neutral-200">
                    <div className="d-flex flex-wrap align-items-end gap-3">
                        <div className="flex-grow-1" style={{ minWidth: '240px', maxWidth: '360px' }}>
                            <label htmlFor="reopen-complaint-search" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:magnify" /> Search
                            </label>
                            <div className="position-relative">
                                <Icon
                                    icon="mdi:magnify"
                                    className="position-absolute top-50 translate-middle-y text-muted"
                                    style={{ left: '14px', fontSize: '20px', pointerEvents: 'none' }}
                                />
                                <input
                                    id="reopen-complaint-search"
                                    type="search"
                                    className="form-control ps-5 py-2 bg-white"
                                    placeholder="Search by complaint number"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div style={{ minWidth: '210px' }}>
                            <label htmlFor="reopen-from" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:calendar-start" /> Re-opened from
                            </label>
                            <input
                                id="reopen-from"
                                type="datetime-local"
                                className="form-control py-2 bg-white"
                                value={fromDate}
                                max={toDate || undefined}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div style={{ minWidth: '210px' }}>
                            <label htmlFor="reopen-to" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:calendar-end" /> Re-opened to
                            </label>
                            <input
                                id="reopen-to"
                                type="datetime-local"
                                className="form-control py-2 bg-white"
                                value={toDate}
                                min={fromDate || undefined}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2 ms-auto">
                            {(fromDate || toDate) && (
                                <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={clearDateRange} title="Clear date range">
                                    <Icon icon="mdi:close" /> Clear
                                </button>
                            )}
                            <span className="badge bg-primary-50 text-primary-600 border border-primary-100 px-12 py-8 radius-8 fw-semibold">
                                {filteredComplaints.length} shown
                            </span>
                        </div>
                    </div>
                </div>
                <div className="row">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <div
                            key={index}
                            className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6"
                            >

                                  <div className="card h-full overflow-hidden border-0 rounded-xl shadow-xs hover:shadow-md transition-all duration-300">
                                    {/* Priority Top Border (you can use a thin red bar for critical) */}
                                    <div className="h-1 w-full bg-red-500" />

                                    <div className="card-body p-5">
                                        <div className="flex flex-col md:flex-row justify-between items-start w-full gap-4">
                                        {/* Left Section */}
                                        {[...Array(7)].map((_, idx) => (
                                            <div className="flex-1 w-full space-y-3">
                                                {/* Complaint ID and Badge */}
                                                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                                                    <Skeleton height={24} width="160px" className="rounded" />
                                                    <Skeleton height={24} width="80px" className="rounded-full" />
                                                </div>


                                                <Skeleton height={20} width="200px" className="rounded" />
                                                <Skeleton height={16} width="140px" className="rounded" />
                                            </div>

                                        ))}


                                        </div>

                                    </div>
                                    </div>


                            </div>
                        ))
                        ) : filteredComplaints.length > 0 ? (
                        filteredComplaints.map((complaint) => {
                            const priority = getPriorityClasses(complaint.priority_level);
                            const timeElapsed = getTimeElapsed(complaint.time_of_call);
                            const qrCodeUrl = qrCodes[complaint._id];
                            return (
                               <div key={complaint._id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6">
                                    <div
                                        className={`complaint-card card h-full overflow-hidden border ${priority.bg} ${priority.border} rounded-xl bg-white/80 backdrop-blur-sm relative ${priority.glow} ${priority.pulse}`}
                                        data-priority={complaint.priority_level}
                                        style={{ cursor: 'default' }}
                                    >
                                        {/* Priority indicator top border */}
                                        <div className={`absolute top-0 left-0 w-full h-1 ${priority.gradient}`}></div>


                                        <div className="card-body p-5">
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start w-100">
                                                {/* Left Content - takes remaining space */}
                                                <div className="flex-grow-1 me-md-3 mb-3 mb-md-0">
                                                    {/* Complaint Number and Status */}
                                                    <div className="d-flex flex-wrap align-items-center mb-2">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-0 me-3" style={{whiteSpace: 'nowrap'}}>
                                                            #{complaint.complaint_number || 'N/A'}
                                                        </h3>
                                                        <div className="d-flex flex-column">
                                                            <span className={`badge text-xs font-semibold ${priority.gradient} px-3 py-1 rounded-full text-white me-2 mb-1`}>
                                                                {complaint.priority_level || 'N/A'}
                                                            </span>
                                                            <span className={`text-xs font-semibold ${priority.text} d-flex align-items-center`}>
                                                                <Icon icon="mdi:clock-outline" className="me-1" style={{ lineHeight: 1 }} />
                                                                Elapsed from time of call {timeElapsed}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Department Info with added space */}
                                                    <table className="w-100 mb-3"> {/* Added mb-3 for space after department */}
                                                        <tbody>
                                                            <tr className="border-top">
                                                                <td className="py-2 text-muted fw-medium w-25" style={{ whiteSpace: 'nowrap' }}>
                                                                    <Icon icon="bi:building" className="me-2 text-muted align-middle" />
                                                                    <span className="align-middle">Department:</span>
                                                                </td>
                                                                <td className="py-2">
                                                                    <span className="text-body">{complaint.dept_id?.name || 'N/A'}</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Right Content - QR Code (keeps its original width) */}
                                                <div className="flex-shrink-0">
                                                     {qrCodeUrl && (
                                                <div className="flex-shrink-0">
                                                <a
                                                    href={qrDataUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none text-reset" style={{zIndex: 9}}
                                                >
                                                    <div
                                                    className="qr-code-container"
                                                    style={{
                                                        width: '90px',
                                                        height: '90px',
                                                        background: 'white',
                                                        padding: '4px',
                                                        borderRadius: '6px',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        cursor: 'pointer'
                                                    }}
                                                    >
                                                    <img
                                                        src={qrCodeUrl}
                                                        alt="Complaint QR Code"
                                                        style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain'
                                                        }}
                                                        onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = '';
                                                        e.target.parentElement.style.display = 'flex';
                                                        e.target.parentElement.innerHTML = `
                                                            <div style="text-align: center; padding: 10px;">
                                                            <div style="font-size: 14px; margin-bottom: 8px;">QR Code Error</div>
                                                            <div style="font-size: 12px; color: #666;">Click to view complaint</div>
                                                            </div>`;
                                                        }}
                                                    />
                                                    </div>
                                                </a>
                                                </div>
                                            )}
                                                </div>
                                            </div>

                                            {/* Complaint details in table format */}
                                           <div className="mt-4 border-top pt-3" >
                                                <table className="table table-responsive scroll-sm">
                                                    <tbody>
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">District</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.complainant_district_id?.name || complaint.district_id?.name || complaint.complaint_district_id?.name || 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:calendar" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Referred By</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.complaint_reffered_by || 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:clock" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Date of Call</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.time_of_call ? new Date(complaint.time_of_call).toLocaleString() : 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:file-earmark-text" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Complaint Nature</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.complaint_nature_id ? complaint.complaint_nature_id?.name : 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:house" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Re-Opened By:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.statusHistory?.[complaint.statusHistory.length - 1]?.by || 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Re-Open Remarks:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.statusHistory?.[complaint.statusHistory.length - 1]?.userRemarks || 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:sticky" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Re-Open Time</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.statusHistory?.[complaint.statusHistory.length - 1]?.changedAt || 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:paperclip" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Re-Open Attachment</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                {(() => {
                                                                    const media = getReopenEntry(complaint)?.userMediaArray || [];
                                                                    if (!media.length) return 'N/A';
                                                                    return (
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {media.map((url, i) => (
                                                                                <a
                                                                                    key={i}
                                                                                    href={url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="btn btn-sm btn-outline-primary py-0 px-2"
                                                                                    style={{ fontSize: '11px' }}
                                                                                >
                                                                                    File {media.length > 1 ? i + 1 : ''}
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                        </tr>

                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12 text-center py-5 text-gray-400">
                            {searchTerm.trim()
                                ? `No re-opened complaint found for "${searchTerm.trim()}".`
                                : (fromDate || toDate)
                                    ? 'No re-opened complaints in the selected date range.'
                                    : 'No re-opened complaints at the moment.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TableDataLayerReopen;
